<template>
  <div>
    <!-- toolbar -->
    <v-toolbar flat color="white" class="mb-4">
      <v-toolbar-title>{{ changeHeaderTitle }}</v-toolbar-title>
      <v-divider class="mx-2" inset vertical></v-divider>
      <v-spacer></v-spacer>
      <v-btn class="primary" @click="BackToRoles">{{
        $t("buttons.rolesBack")
      }}</v-btn>
    </v-toolbar>
    <formWrapper :validator="$v.form">
      <v-form @submit.prevent="handleSubmit">
        <!-- <v-container> -->
        <v-row class="bg-white pa-3 mb-3">
          <v-col cols="12" lg="6">
            <form-group attribute="name" name="name">
              <template #default="{ attrs,listeners }">
                <v-text-field
                  outlined
                  v-on="listeners"
                  v-bind="attrs"
                  v-model="form.name"
                  label="Name"
                ></v-text-field>
              </template>
            </form-group>
          </v-col>
          <v-col cols="12" lg="6">
            <v-text-field
              outlined
              v-model="form.display_name"
              :label="$t('labels.display_name')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" lg="6">
            <form-group attribute="description" name="description">
              <template #default="{ attrs, listeners }">
                <v-textarea
                  v-bind="attrs"
                  outlined
                  v-on="listeners"
                  label="Description"
                  value
                  rows="1"
                  v-model="form.description"
                ></v-textarea>
              </template>
            </form-group>
          </v-col>
          <v-col cols="12" lg="6">
            <v-autocomplete
              eager
              :disabled="loadingPermissions"
              prepend-inner-icon="mdi-magnify"
              :loading="loadingPermissions"
              class="select-permissions"
              label=""
              outlined
              v-model="form.permissions_ids"
              :items="permissions"
              item-text="display_name"
              item-value="id"
              chips
              small-chips
              deletable-chips
              :placeholder="$t('select_permissions')"
              multiple
              clearable
              return-object
              @update:search-input="onSearch"
              :no-data-text="$t('no_data')"
            >
              <template #prepend-item>
                <v-list-item
                  v-if="permissions.length > 0 && showSelectAll"
                  @mousedown.prevent
                  @click.prevent="onSelectAllPermission"
                >
                  <v-list-item-title class="d-flex align-center">
                    <v-icon
                      :color="form.permissions_ids.length > 0 ? 'primary ' : ''"
                    >
                      {{ icon }}
                    </v-icon>
                    {{ $t("selectAll") }}
                  </v-list-item-title>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item
                  v-on.prevent="on"
                  v-bind="attrs"
                  #default="{ active }"
                >
                  <v-list-item-action>
                    <v-checkbox :input-value="active"></v-checkbox>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>
                      <v-row no-gutters align="center">
                        <span>{{ item.display_name }}</span>
                      </v-row>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>
          <v-col cols="12">
            <v-btn
              :disabled="$v.form.$invalid"
              :loading="loading"
              type="submit"
              large
              color="success"
              class="ma-0"
              >{{ changeTitle }}</v-btn
            >
          </v-col>
        </v-row>
      </v-form>
    </formWrapper>
  </div>
</template>

<script>
import { required, maxLength, minLength } from "vuelidate/lib/validators";
import { maxWords } from "@/utils/validate";
import { IndexData } from "@/helpers/apiMethods";
import { StoreData } from "../../helpers/apiMethods";

export default {
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(30),
        minLength: minLength(3)
      },
      description: { maxWords: maxWords(500) }
    }
  },
  data() {
    return {
      loadingPermissions: true,
      permissions: [],
      locale: this.locale,
      form: {
        permissions_ids: [],
        name: "",
        description: "",
        display_name: ""
      },
      isEdit: false,
      loading: false,
      showSelectAll: true
    };
  },

  created() {
    this.handleGetPermissions();
    if (this.$route.params.id) {
      this.isEdit = true;
      this.handleShow();
    }
  },

  computed: {
    selectAllPermission() {
      return this.form.permissions_ids.length === this.permissions.length;
    },

    selectSomePermission() {
      return this.form.permissions_ids.length > 0 && !this.selectAllPermission;
    },

    icon() {
      if (this.selectAllPermission) return "mdi-checkbox-marked";
      if (this.selectSomePermission) return "mdi-minus-box";
      return "mdi-checkbox-blank-outline";
    },
    changeTitle() {
      return this.isEdit ? this.$t("buttons.edit") : this.$t("buttons.create");
    },
    changeHeaderTitle() {
      return this.isEdit
        ? this.$t("headings.edit")
        : this.$t("headings.create");
    }
  },
  methods: {
    async handleShow() {
      const res = await IndexData({
        reqName: `roles/${this.$route.params.id}`
      });
      const {
        role: { permissions, ...props }
      } = res.data;
      this.form = { ...props };
      this.form.permissions_ids = permissions.map(per => per.id);
    },
    onSelectAllPermission() {
      this.$nextTick(() => {
        if (this.selectAllPermission) {
          this.form.permissions_ids = [];
        } else {
          this.form.permissions_ids = this.permissions.slice();
        }
      });
    },
    onSearch(search) {
      if (search) {
        this.showSelectAll = this.permissions.some(per =>
          per.display_name.toLowerCase().match(search)
        );
      }
    },
    async handleGetPermissions() {
      const res = await IndexData({ reqName: "permissions" });
      const { permissions } = res.data;
      for (const key in permissions) {
        this.permissions.push(...permissions[key]);
      }
      this.loadingPermissions = false;
    },
    handleSubmit() {
      this.loading = true;
      if (!this.isEdit) {
        // in case create
        this.onSubmit(`roles`);
      } else {
        // in case update
        this.onSubmit(`roles/${this.$route.params.id}`);
      }
    },

    generateData() {
      const formData = new FormData();
      formData.append("name", this.form.name);
      this.form.description &&
        formData.append("description", this.form.description);
      this.form.display_name &&
        formData.append("display_name", this.form.display_name);

      this.form.permissions_ids.length > 0 &&
        this.form.permissions_ids.forEach((per, index) => {
          formData.append(`permissions_ids[${index}]`, per.id);
        });
      if (this.isEdit) {
        formData.append("locale", this.locale);
        formData.append("_method", "put");
      }
      return formData;
    },
    async onSubmit(reqName) {
      try {
        await StoreData({ reqName, data: this.generateData() });
        this.BackToRoles();
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    BackToRoles() {
      this.$router.push("/roles");
    }
  }
};
</script>
